import React from 'react'
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from 'recharts'
import { AnalysisHistoricalResults, ScoreEntry, ScoreData } from '../../types'
import usePadding from '../../hooks/useChartBarsPositions'
import { formatDate, getBarColor } from '../../utils/chartUtils'
import { t } from 'i18next'
import RenderCustomLegend from '../RenderCustomLegend'
interface EvolutionEmotionalBarChartProps {
  data: AnalysisHistoricalResults | null
  scoreKey: string
  lowLabel?: string
  mediumLabel?: string
  highLabel?: string
  yAxisLabels: string[]
}

const EvolutionEmotionalBarChart: React.FC<EvolutionEmotionalBarChartProps> = ({
  data,
  scoreKey,
  lowLabel,
  mediumLabel,
  highLabel,
}) => {
  const padding = usePadding()

  const scoreData = data?.data?.attributes?.scores
    ?.find((s: ScoreData) => s.name === scoreKey)
    ?.data.map((item: ScoreEntry) => ({
      date: item.date,
      score: item.score,
      rating: Number(item.rating),
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  let minScore
  let maxScore

  switch (scoreKey) {
    case 'FAGERSTRON':
      minScore = 0
      maxScore = 6
      break
    case 'RICHMOND':
      minScore = 0
      maxScore = 10
      break
    case 'AUDIT_C':
      minScore = 0
      maxScore = 12
      break
    default:
      minScore = 0
      maxScore = 10
  }

  const validData = scoreData ? scoreData.slice(-3) : []

  if (!validData.length) {
    console.warn(`${scoreKey} no está disponible o no tiene datos.`)
  }

  const getYAxisConfig = (chartType: string) => {
    switch (chartType) {
      case 'FAGERSTRON':
        return {
          ticks: [2, 4, 6],
          labels: t('myEvolution.tobaccoAndAlcohol.chart.yAxisLabels', {
            returnObjects: true,
          }) as string[],
        }
      case 'RICHMOND':
        return {
          ticks: [3, 5, 7, 10],
          labels: t('myEvolution.tobaccoAndAlcohol.chart.yAxisLabels2', {
            returnObjects: true,
          }) as string[],
        }
      case 'AUDIT_C':
        return {
          ticks: [3, 6],
          labels: t('myEvolution.tobaccoAndAlcohol.chart.yAxisLabels3', {
            returnObjects: true,
          }) as string[],
        }
      default:
        return {
          ticks: [],
          labels: [],
        }
    }
  }

  const { ticks, labels } = getYAxisConfig(scoreKey)

  return (
    <div className="w-[100%] sm:w-[60%]">
      <ResponsiveContainer height={350}>
        <BarChart
          data={validData}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        >
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            scale="point"
            padding={padding}
            axisLine={true}
            tickLine={true}
            tick={{ fontSize: 16 }}
          />

          <YAxis
            domain={[minScore, maxScore]}
            ticks={ticks}
            tickFormatter={(value) => {
              const index = ticks.indexOf(value)
              return labels[index] !== undefined ? labels[index] : ''
            }}
            interval={0}
            tickCount={ticks.length}
            width={100}
            tick={{ fontSize: 16 }}
          />

          <ReferenceLine y={ticks[0]} stroke="#0E8343" strokeDasharray="3 3" />
          <ReferenceLine
            y={ticks[1]}
            stroke={scoreKey === 'AUDIT_C' ? '#FF0000' : '#F7E06D'}
            strokeDasharray="3 3"
          />
          <ReferenceLine y={ticks[2]} stroke="#AD941A" strokeDasharray="3 3" />
          <ReferenceLine y={ticks[3]} stroke="#A52222" strokeDasharray="3 3" />

          <Tooltip
            formatter={(value) =>
              value === 'not available' ? t('myEvolution.valueNotAvailable') : `${value} pts`
            }
            labelFormatter={formatDate}
          />
          <Legend
            content={
              <RenderCustomLegend
                lowLabel={lowLabel || ''}
                mediumLabel={mediumLabel || ''}
                highLabel={highLabel || ''}
              />
            }
          />

          <Bar
            dataKey="score"
            barSize={40}
            fillOpacity={1}
            stroke="none"
            name="Puntuación"
            minPointSize={2}
          >
            {validData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={getBarColor(entry)} 
                style={{ visibility: entry.score.toString() === 'not available' ? 'hidden' : 'visible' }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default EvolutionEmotionalBarChart
